import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, X, Loader2, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Devotees ask to receive the daily darshan on WhatsApp.
export default function DarshanSubscribe({ variant = "inline" }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || phone.replace(/\D/g, "").length < 8) {
      toast.error(t("reminder_invalid"));
      return;
    }
    setSending(true);
    try {
      await api.post("/subscribers", { name: name.trim(), phone: phone.trim(), language: lang });
      setDone(true);
      toast.success(t("subscribe_success"));
      setTimeout(() => { setOpen(false); setDone(false); setName(""); setPhone(""); }, 1800);
    } catch {
      toast.error(t("subscribe_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          variant === "inline"
            ? "btn-ghost-gold flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
            : "flex items-center gap-2 text-sm font-serif font-semibold text-tara-goldDeep hover:gap-3 transition-all"
        }
        data-testid="darshan-subscribe-btn"
      >
        <Sun className="h-4 w-4" /> {t("subscribe_cta")}
      </button>

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#14262A]/55 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 26, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.97 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="glass-strong relative w-full max-w-md rounded-[1.6rem] p-7"
              data-testid="subscribe-modal"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-5 top-5 text-tara-muted hover:text-tara-sindoor transition-colors"
                data-testid="subscribe-close"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {done ? (
                <div className="py-10 text-center" data-testid="subscribe-done">
                  <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-tara-gold to-tara-vermilion">
                    <Check className="h-7 w-7 text-white" />
                  </span>
                  <p className="font-display text-2xl text-tara-ink">{t("subscribe_success")}</p>
                </div>
              ) : (
                <>
                  <span className="badge-gold inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] font-serif uppercase tracking-[0.24em] mb-4">
                    <Sun className="h-3 w-3" /> {t("darshan_title")}
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-tara-ink mb-2">{t("subscribe_title")}</h3>
                  <p className="text-sm text-tara-body leading-relaxed mb-5">{t("subscribe_sub")}</p>

                  <form onSubmit={submit} className="space-y-3" data-testid="subscribe-form">
                    <input
                      value={name} onChange={(e) => setName(e.target.value)}
                      placeholder={t("field_name")}
                      className="w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60"
                      data-testid="subscribe-name-input"
                    />
                    <input
                      value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder={t("reminder_phone")}
                      className="w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60"
                      data-testid="subscribe-phone-input"
                    />
                    <button
                      type="submit" disabled={sending}
                      className="btn-sacred flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
                      data-testid="subscribe-submit-btn"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                      {t("subscribe_confirm")}
                    </button>
                    <p className="text-[11px] text-tara-muted leading-relaxed">{t("subscribe_privacy")}</p>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
